import { useCallback } from "react";

/**
 * Returns a change-event handler that only updates a value when it satisfies
 * a constraint predicate. If the predicate fails, the value is reset to
 * `defaultValue` instead of storing an invalid entry.
 *
 * Avoids scattering validation logic across each individual input component —
 * the constraint is declared once and the handler is dropped onto an `onChange`.
 *
 * @param {(value: number) => void} setValue - State setter for the controlled input
 * @param {(value: number) => boolean} constraint - Predicate; return true to accept the value
 * @param {number} [defaultValue=0] - Value to use when the constraint is not satisfied
 * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
 */
export const useConstraint = (setValue, constraint, defaultValue = 0) => {
  return useCallback((event) => {
    const value = +event.target.value;
    if (constraint(value)) {
      setValue(value);
    } else {
      setValue(defaultValue);
    }
  }, [setValue, constraint, defaultValue]);
};

/**
 * Predicate that accepts any non-negative number.
 * Used with `useConstraint` to prevent negative inputs on fields like mass or radius.
 *
 * @param {number} value
 * @returns {boolean}
 */
export const isPositiveConstraint = (value) => value >= 0;
